// The kadmon panel's memory — TODO T55 (9 Sep 2026), agents doc § 7.2.
//
// TWO READS, POLLED, AND NO CACHE OF THE CONVERSATION IN THIS APP. The chat lives in the
// database; the panel draws what the service returns. That is the polling model applied to a
// surface that looks like a chat app (CLAUDE.md § Brain tier: "polling not sockets"), and it is
// what makes one shared history work — Lorenz continuing a chat Mona started sees her turns
// because he asked the service for them, not because anything was handed between browsers.
//
// The ask itself is `api/find.ts`: a POST that ends in a refetch of these two keys, like every
// other write in the brain (`useConsoleMutation`).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ChatDetail, ChatResponse, ChatSummary, ChatsResponse } from '@vera/bff';
import { API_PREFIX, type RequestFn } from './core.ts';
import { POLL_MS } from './intervals.ts';
import { useRequest } from './provider.tsx';

export const chatsApi = (request: RequestFn) => ({
  list: async () => (await request<ChatsResponse>(`${API_PREFIX}/chats`)).chats,
  get: async (chatId: string) =>
    (await request<ChatResponse>(`${API_PREFIX}/chats/${chatId}`)).chat,
});

/** Every chat in the tenant, newest activity first — the panel's left column. */
export function useChats(open: boolean): UseQueryResult<ChatSummary[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['chats'],
    queryFn: () => chatsApi(request).list(),
    // Only while the panel is open: this is not a screen, and a poll behind a closed drawer is a
    // request per recruiter per interval for something nobody is looking at.
    enabled: open,
    refetchInterval: POLL_MS.chats,
  });
}

/** One chat and its turns. Not asked at all for a chat that has not been started yet. */
export function useChat(chatId: string | null): UseQueryResult<ChatDetail> {
  const request = useRequest();
  return useQuery({
    queryKey: ['chat', chatId],
    queryFn: () => chatsApi(request).get(chatId!),
    enabled: chatId !== null,
    refetchInterval: POLL_MS.chats,
  });
}
